import { countdownTarget } from '@content-scheduler/shared/render';
import { Buttons } from '../parts/Buttons';
import { Countdown } from '../parts/Countdown';
import { CardLayout, OverlayLayout, SplitLayout, layouts } from '../parts/Layouts';
import parts from '../parts/parts.module.css';
import type { TemplateProps } from '../types';
import styles from './fundraiser.module.css';
import { formatMoney, progressOf } from './money';

type ProgressData = TemplateProps<'fundraiser'>['doc']['fields']['progress'];

function ProgressBar({ progress }: { progress: ProgressData }) {
    const { raised, goal, currency, supporters } = progress;
    const { percent, barPercent } = progressOf(raised, goal);
    return (
        <div className={`${styles.progress} ${layouts.afterText}`}>
            <p className={styles.amounts}>
                <span className={styles.raised}>{formatMoney(raised, currency)}</span> raised of{' '}
                {formatMoney(goal, currency)}
            </p>
            <div
                className={styles.track}
                role="progressbar"
                aria-label="Fundraising progress"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(barPercent)}
            >
                <div className={styles.fill} style={{ width: `${barPercent}%` }} />
            </div>
            <p className={styles.stats}>
                <span>{percent}% of goal</span>
                {supporters !== null && (
                    <span>
                        {supporters.toLocaleString()}{' '}
                        {supporters === 1 ? 'supporter' : 'supporters'}
                    </span>
                )}
            </p>
        </div>
    );
}

export function Fundraiser({ doc, ctx, onDismiss }: TemplateProps<'fundraiser'>) {
    const { headline, body, progress, buttons, image, countdown } = doc.fields;
    const target = countdownTarget(doc, ctx.schedule);

    const content = (
        <>
            <h2 className={parts.headline}>{headline}</h2>
            {body.trim() && <p className={parts.body}>{body}</p>}
            {progress.show && <ProgressBar progress={progress} />}
            <Countdown countdown={countdown} target={target} className={layouts.afterText} />
            <Buttons buttons={buttons} className={layouts.afterText} />
        </>
    );

    if (doc.variant === 'story-split') {
        return (
            <SplitLayout image={image} onDismiss={onDismiss}>
                {content}
            </SplitLayout>
        );
    }
    if (doc.variant === 'image-background') {
        return (
            <OverlayLayout image={image} onDismiss={onDismiss}>
                {content}
            </OverlayLayout>
        );
    }
    return (
        <CardLayout image={image} onDismiss={onDismiss}>
            {content}
        </CardLayout>
    );
}
